/**
 * @file src/_modulos/prenota/tabela/layouts/config/hooks/useTableUtils.ts
 * @description Utilitários simplificados para a tabela
 * 
 * Princípios aplicados:
 * - KISS: Funções simples e diretas
 * - SRP: Cada função tem uma responsabilidade única
 * - YAGNI: Remove funcionalidades não utilizadas
 */

import type { FetchPrenotasResponse } from "@PN/tabela";

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS DE FORMATAÇÃO                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Formata número com separadores de milhares
 */
export function formatNumber(value: number | undefined): string {
  if (value === undefined || value === null) return "0";
  return value.toLocaleString("pt-BR");
}

/**
 * Retorna o label da tab
 */
export function getTabLabel(tab: string): string {
  const labels: Record<string, string> = {
    todas: "pré-notas",
    minhas: "minhas pré-notas",
    pendentes: "pré-notas pendentes",
    revisao: "pré-notas para revisão",
  };
  return labels[tab] || "itens";
}

/* -------------------------------------------------------------------------- */
/* UTILITÁRIOS DE CÁLCULO                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Calcula estatísticas dos dados
 */
export function calculateStats(data?: FetchPrenotasResponse) {
  if (!data) {
    return {
      totalItems: 0,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
    };
  }

  return {
    totalItems: data.total ?? 0,
    currentPage: data.page ?? 1,
    totalPages: data.totalPages ?? 1,
    pageSize: data.pageSize ?? 10,
  };
}

/**
 * Gera texto de resumo das estatísticas
 */
export function generateSummaryText(
  stats: ReturnType<typeof calculateStats>,
  tab: string
): string {
  const { totalItems, currentPage, totalPages } = stats;

  if (totalItems === 0) return "Nenhum item encontrado";

  const tabLabel = getTabLabel(tab);
  const pageInfo = totalPages > 1 ? ` (página ${currentPage} de ${totalPages})` : "";

  return `${formatNumber(totalItems)} ${tabLabel}${pageInfo}`;
}

/**
 * Calcula estatísticas de filiais
 */
export function calculateFilialStats(filials: string[], accessibleFilials: string[]) {
  const totalAccessible = accessibleFilials.length;
  const selectedCount = filials.length;
  const isAllSelected = selectedCount === totalAccessible;

  return {
    totalAccessible,
    selectedCount,
    isAllSelected,
    displayText: isAllSelected
      ? "Todas as filiais"
      : `${selectedCount} de ${totalAccessible} filiais`,
  };
} 